// store/api/listApi.ts
//
// Suppression list APIs (email / name / website). The browser talks to our own
// Express proxy at /api/suppression-lists/* (mounted in packages/server/routes.ts),
// which resolves the user from the session and forwards to the
// Infynd-CL-List-Management service (lists of type `suppression`, subtype = the
// suppression kind). The upstream bearer token stays server side.
//
// Endpoint map (proxy -> list-management):
//   GET    /api/suppression-lists/:kind/lists           getLists
//   GET    /api/suppression-lists/:kind/data            getData
//   GET    /api/suppression-lists/:kind/dropdown        getDropdown
//   POST   /api/suppression-lists/:kind/save            saveList
//   POST   /api/suppression-lists/:kind/delete-list     deleteList
//   POST   /api/suppression-lists/:kind/delete-lists    deleteLists
//   POST   /api/suppression-lists/:kind/delete-items    deleteItems
//   POST   /api/suppression-lists/:kind/check-name      checkName

import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './axiosBaseQuery';

export type SuppressionKind = 'email' | 'name' | 'website';

// ========================================
// SHARED RESPONSE TYPES
// ========================================

export interface SuppressionListsResponse {
  lists: Array<{ name: string; total: number; dateModified: string }>;
  total: number;
}

export interface SuppressionDataResponse {
  emails?: Array<Record<string, unknown>>;
  names?: Array<Record<string, unknown>>;
  websites?: Array<Record<string, unknown>>;
  companies?: Array<Record<string, unknown>>;
  items: Array<Record<string, unknown>>;
  total: number;
  count: number;
}

export interface SuppressionMutationResponse {
  success: boolean;
  message?: string;
  res?: string;
  count?: number;
}

export interface SuppressionCheckNameResponse {
  exists: boolean;
}

// ========================================
// API FACTORY
// ========================================

/**
 * The three suppression APIs are structurally identical — only the reducer path,
 * tag names and URL kind differ. Building them from one factory keeps the
 * contract in a single place.
 */
function createSuppressionApi<Kind extends SuppressionKind>(
  kind: Kind,
  reducerPath: string,
  listTag: string,
  dropdownTag: string
) {
  return createApi({
    reducerPath,
    baseQuery: axiosBaseQuery('/api/suppression-lists'),
    tagTypes: [listTag, dropdownTag],
    endpoints: (build) => ({
      getLists: build.query<
        SuppressionListsResponse,
        { userId: string; limit?: number; start?: number; searchword?: string }
      >({
        query: (payload) => ({
          url: `/${kind}/lists`,
          method: 'GET',
          params: {
            userId: payload.userId,
            limit: payload.limit ?? 25,
            start: payload.start ?? 0,
            searchword: payload.searchword ?? '',
          },
        }),
        providesTags: [{ type: listTag, id: 'LIST' }],
      }),

      getData: build.mutation<
        SuppressionDataResponse,
        { userId: string; listName: string; searchword?: string; limit?: number; start?: number }
      >({
        query: (payload) => ({
          url: `/${kind}/data`,
          method: 'GET',
          params: {
            userId: payload.userId,
            listName: payload.listName,
            limit: payload.limit ?? 25,
            start: payload.start ?? 0,
            ...(payload.searchword ? { searchword: payload.searchword } : {}),
          },
        }),
      }),

      getDropdown: build.query<{ lists: { name: string; _id: string }[] }, { userId: string; searchword?: string }>({
        query: (payload) => ({
          url: `/${kind}/dropdown`,
          method: 'GET',
          params: {
            userId: payload.userId,
            ...(payload.searchword ? { searchword: payload.searchword } : {}),
          },
        }),
        providesTags: [{ type: dropdownTag, id: 'LIST' }],
      }),

      saveList: build.mutation<SuppressionMutationResponse, Record<string, any>>({
        query: (payload) => ({ url: `/${kind}/save`, method: 'POST', body: payload }),
        invalidatesTags: [
          { type: listTag, id: 'LIST' },
          { type: dropdownTag, id: 'LIST' },
        ],
      }),

      deleteList: build.mutation<
        SuppressionMutationResponse,
        { userId: string; listName: string }
      >({
        query: (payload) => ({ url: `/${kind}/delete-list`, method: 'POST', body: payload }),
        invalidatesTags: [
          { type: listTag, id: 'LIST' },
          { type: dropdownTag, id: 'LIST' },
        ],
      }),

      deleteLists: build.mutation<
        SuppressionMutationResponse,
        { userId: string; listNames: string[] }
      >({
        query: (payload) => ({ url: `/${kind}/delete-lists`, method: 'POST', body: payload }),
        invalidatesTags: [
          { type: listTag, id: 'LIST' },
          { type: dropdownTag, id: 'LIST' },
        ],
      }),

      deleteItems: build.mutation<SuppressionMutationResponse, Record<string, any>>({
        query: (payload) => ({ url: `/${kind}/delete-items`, method: 'POST', body: payload }),
        invalidatesTags: [{ type: listTag, id: 'LIST' }],
      }),

      checkName: build.mutation<
        SuppressionCheckNameResponse,
        { userId: string; listName: string }
      >({
        query: (payload) => ({ url: `/${kind}/check-name`, method: 'POST', body: payload }),
      }),

      renameList: build.mutation<
        SuppressionMutationResponse,
        { userId: string; listName: string; newName: string }
      >({
        query: (payload) => ({ url: `/${kind}/rename`, method: 'PATCH', body: payload }),
        invalidatesTags: [
          { type: listTag, id: 'LIST' },
          { type: dropdownTag, id: 'LIST' },
        ],
      }),

      copyList: build.mutation<
        SuppressionMutationResponse & { id?: string; name?: string; count?: number },
        { userId: string; listName: string; newListName: string }
      >({
        query: (payload) => ({ url: `/${kind}/copy`, method: 'POST', body: payload }),
        invalidatesTags: [
          { type: listTag, id: 'LIST' },
          { type: dropdownTag, id: 'LIST' },
        ],
      }),
    }),
  });
}

// ========================================
// THE THREE APIS
// ========================================

export const emailSuppressionApi = createSuppressionApi(
  'email',
  'emailSuppressionApi',
  'EmailSuppressionList',
  'EmailSuppressionDropdown'
);

export const nameSuppressionApi = createSuppressionApi(
  'name',
  'nameSuppressionApi',
  'NameSuppressionList',
  'NameSuppressionDropdown'
);

export const websiteSuppressionApi = createSuppressionApi(
  'website',
  'websiteSuppressionApi',
  'WebsiteSuppressionList',
  'WebsiteSuppressionDropdown'
);

// ========================================
// HOOKS — named to match the upstream contract
// ========================================

export const {
  useGetListsQuery: useGetEmailSuppressionListsQuery,
  useLazyGetListsQuery: useLazyGetEmailSuppressionListsQuery,
  useGetDataMutation: useGetEmailSuppressionDataMutation,
  useGetDropdownQuery: useGetEmailSuppressionDropdownQuery,
  useLazyGetDropdownQuery: useLazyGetEmailSuppressionDropdownQuery,
  useSaveListMutation: useSaveEmailSuppressionListMutation,
  useDeleteListMutation: useDeleteEmailSuppressionListMutation,
  useDeleteListsMutation: useDeleteEmailSuppressionListsMutation,
  useDeleteItemsMutation: useDeleteEmailSuppressionItemsMutation,
  useCheckNameMutation: useCheckEmailSuppressionNameMutation,
  useRenameListMutation: useRenameEmailSuppressionListMutation,
  useCopyListMutation: useCopyEmailSuppressionListMutation,
} = emailSuppressionApi;

export const {
  useGetListsQuery: useGetNameSuppressionListsQuery,
  useLazyGetListsQuery: useLazyGetNameSuppressionListsQuery,
  useGetDataMutation: useGetNameSuppressionDataMutation,
  useGetDropdownQuery: useGetNameSuppressionDropdownQuery,
  useLazyGetDropdownQuery: useLazyGetNameSuppressionDropdownQuery,
  useSaveListMutation: useSaveNameSuppressionListMutation,
  useDeleteListMutation: useDeleteNameSuppressionListMutation,
  useDeleteListsMutation: useDeleteNameSuppressionListsMutation,
  useDeleteItemsMutation: useDeleteNameSuppressionItemsMutation,
  useCheckNameMutation: useCheckNameSuppressionNameMutation,
  useRenameListMutation: useRenameNameSuppressionListMutation,
  useCopyListMutation: useCopyNameSuppressionListMutation,
} = nameSuppressionApi;

export const {
  useGetListsQuery: useGetWebsiteSuppressionListsQuery,
  useLazyGetListsQuery: useLazyGetWebsiteSuppressionListsQuery,
  useGetDataMutation: useGetWebsiteSuppressionDataMutation,
  useGetDropdownQuery: useGetWebsiteSuppressionDropdownQuery,
  useLazyGetDropdownQuery: useLazyGetWebsiteSuppressionDropdownQuery,
  useSaveListMutation: useSaveWebsiteSuppressionListMutation,
  useDeleteListMutation: useDeleteWebsiteSuppressionListMutation,
  useDeleteListsMutation: useDeleteWebsiteSuppressionListsMutation,
  useDeleteItemsMutation: useDeleteWebsiteSuppressionItemsMutation,
  useCheckNameMutation: useCheckWebsiteSuppressionNameMutation,
  useRenameListMutation: useRenameWebsiteSuppressionListMutation,
  useCopyListMutation: useCopyWebsiteSuppressionListMutation,
} = websiteSuppressionApi;
